import { useEffect, useState } from 'react';

import { reviewEventStreamPayloadSchema } from '../../shared/contracts/review-stream.ts';
import type { DeckHistory, DeckHistoryEvent } from './deck-page.ts';

type StreamEvent = {
  data: string;
  lastEventId?: string;
};

export type ReviewEventSource = {
  addEventListener(
    type: 'review' | 'reset',
    listener: (event: StreamEvent) => void,
  ): void;
  close(): void;
};

type StreamControllerInput = {
  deckId: string;
  createSource?: (url: string) => ReviewEventSource;
  fetchSnapshot: () => Promise<DeckHistory>;
  onError: (error: Error | null) => void;
  onHistory: (history: DeckHistory) => void;
  snapshot: DeckHistory;
};

const emptyCursor = 'v1.MA';

export function createReviewEventStreamController(
  input: StreamControllerInput,
) {
  let snapshot = input.snapshot;
  let source: ReviewEventSource | null = null;
  let closed = false;
  let createSource: ((url: string) => ReviewEventSource) | undefined;
  const seenIds = new Set(snapshot.events.map((event) => event.id));
  const seenCursors = new Set([snapshot.cursor ?? emptyCursor]);

  const applySnapshot = (next: DeckHistory) => {
    snapshot = next;
    seenIds.clear();
    seenCursors.clear();
    for (const event of next.events) seenIds.add(event.id);
    seenCursors.add(next.cursor ?? emptyCursor);
    input.onHistory(next);
  };

  const applyReview = (message: StreamEvent) => {
    try {
      const parsed = reviewEventStreamPayloadSchema.safeParse(
        JSON.parse(message.data),
      );
      if (!parsed.success) return;
      const cursor = parsed.data.cursor ?? message.lastEventId;
      const event: DeckHistoryEvent = parsed.data.event;
      if (!cursor || !event || seenIds.has(event.id) || seenCursors.has(cursor))
        return;
      seenIds.add(event.id);
      seenCursors.add(cursor);
      snapshot = {
        ...snapshot,
        cursor,
        events: [...snapshot.events, event],
      };
      input.onHistory(snapshot);
    } catch {
      // A malformed delivery cannot be applied over the last good snapshot.
    }
  };

  const connect = () => {
    if (closed || !createSource) return;
    source = createSource(
      `/api/decks/${encodeURIComponent(input.deckId)}/review-events/stream?cursor=${encodeURIComponent(snapshot.cursor ?? emptyCursor)}`,
    );
    source.addEventListener('review', applyReview);
    source.addEventListener('reset', () => void reset());
  };

  const reset = async () => {
    source?.close();
    source = null;
    try {
      const next = await input.fetchSnapshot();
      if (closed) return;
      applySnapshot(next);
      input.onError(null);
      connect();
    } catch (error) {
      if (closed) return;
      input.onError(
        error instanceof Error
          ? error
          : new Error('Deck history refresh failed'),
      );
    }
  };

  return {
    start() {
      if (closed) return;
      createSource =
        input.createSource ?? ((url: string) => new EventSource(url));
      connect();
    },
    stop() {
      if (closed) return;
      closed = true;
      source?.close();
      source = null;
    },
  };
}

export function useReviewEventStream(
  deckId: string | undefined,
  history: DeckHistory | undefined,
  fetchSnapshot: () => Promise<DeckHistory>,
) {
  const [streamedHistory, setStreamedHistory] = useState(history);
  const [streamError, setStreamError] = useState<Error | null>(null);
  useEffect(() => {
    if (!deckId || !history) {
      setStreamedHistory(history);
      setStreamError(null);
      return;
    }
    setStreamError(null);
    setStreamedHistory(history);
    const controller = createReviewEventStreamController({
      deckId,
      fetchSnapshot,
      onError: setStreamError,
      onHistory: setStreamedHistory,
      snapshot: history,
    });
    controller.start();
    return () => controller.stop();
  }, [deckId, fetchSnapshot, history]);

  return { history: streamedHistory, error: streamError };
}
